/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { formatOnOff } from "../utils";

const SRiuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const sriuAlarms = parsedAttributes.sriuAlarms;
  const sriuAlarmsMask = parsedAttributes.sriuAlarmsMask;
  const hasAlarms = sriuAlarms !== undefined || sriuAlarmsMask !== undefined;
  const sriuRfAlarm = Array.isArray(parsedAttributes.sriuRfAlram)
    ? parsedAttributes.sriuRfAlram
    : [];
  const sriuRfAlarmSorted = [...sriuRfAlarm].sort(
    (a, b) =>
      Number(a?.sriuChannelNumber) -
      Number(b?.sriuChannelNumber)
  );
  const hasRfAlarm = sriuRfAlarmSorted.length > 0;
  const isDisconnected = statusLabel === "Disconnected";

  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Overall Alarm" },
    { bit: 2, name: "Fan Fault Alarm" },
    { bit: 3, name: "Temperature Alarm" },
    { bit: 4, name: "DC Voltage Lower Alarm" },
    { bit: 5, name: "Firmware Mismatch Alarm" },
  ];

  if (alarmsOnly) {
    if (!hasAlarms) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">Alarms</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AlarmTable
            alarms={sriuAlarms}
            alarmsMask={sriuAlarmsMask}
            alarmDefinitions={alarmDefinitions}
            isDisconnected={isDisconnected}
          />
        </CardContent>
      </Card>
    );
  }

  const ports = Array.from({ length: 8 }, (_, i) => i + 1);
  const hasRfInfo = ports.some((port) => (
    parsedAttributes[`sriuBandSwitch${port}`] !== undefined ||
    parsedAttributes[`sriuDlPwrIn${port}`] !== undefined
  ));

  if (!hasRfInfo && !hasAlarms && !hasRfAlarm) return null;

  const formatPowerNoUnit = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toFixed(2);
  };

  const getAttr = (prefix: string, port: number): unknown => {
    return parsedAttributes[`sriu${prefix}${port}`];
  };

  return (
    <>
      {hasRfInfo && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px]">Parameter</TableHead>
                    {ports.map((port) => (
                      <TableHead key={port} className="min-w-[90px] text-center">Port {port}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Switch</TableCell>
                    {ports.map((port) => (
                      <TableCell key={`switch-${port}`} className="text-center">
                        {formatOnOff(getAttr("BandSwitch", port))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Downlink Power</TableCell>
                    {ports.map((port) => (
                      <TableCell key={`dl-${port}`} className="text-center">
                        {formatPowerNoUnit(getAttr("DlPwrIn", port))}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {hasRfAlarm && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Alarm</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {sriuRfAlarmSorted.map((rfAlarm: any, index: number) => {
                const channelNumber = rfAlarm?.sriuChannelNumber;
                const isAlarmActive = Number(rfAlarm?.sriuRfAlarms) > 0;

                return (
                  <div
                    key={`${channelNumber ?? index}`}
                    className="rounded-xl border p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">
                        Channel {channelNumber ?? index + 1}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: isAlarmActive ? "#ef4444" : "#22c55e",
                          boxShadow: `0 0 8px ${isAlarmActive ? "#ef4444" : "#22c55e"}`,
                        }}
                      />
                    </div>

                    <div className="mt-3 text-xs text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={sriuAlarms}
              alarmsMask={sriuAlarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default SRiuDeviceDetails;
